import React, { Dispatch, SetStateAction } from 'react';
import { useParams } from 'react-router';

import { DataTableProvider } from '@/shared/components/ui/data-table/context/data-table-context';
import { TData } from '@/shared/components/ui/data-table/context/data-table-context.types';
import { InnerDataTable } from '@/shared/components/ui/data-table/variant/inner-data-table/inner-data-table';
import {
  FilterScopeProvider,
  useFilterScope,
} from '@/shared/features/filter-preferences/context/filter-scope-context';
import { useGroupsConfig } from '@/shared/features/groups/context/groups-config-context';
import { useGroupedResourceQueryParams } from '@/shared/features/groups/hooks/use-grouped-resource-query-params/use-grouped-resource-query-params';
import { usePermissionCheck } from '@/shared/features/permissions/hooks/use-permission-check/use-permission-check';
import { useRbacConfig } from '@/shared/features/rbac/context/rbac-config-context';
import { useGroupRolesListDefinitions } from '@/shared/features/rbac/hooks/use-roles-list-definitions/use-roles-list-definitions';
import { useRolesTransformer } from '@/shared/features/rbac/hooks/use-roles-transformer/use-roles-transformer';
import { TTransformedRole } from '@/shared/features/rbac/hooks/use-roles-transformer/use-roles-transformer.types';
import { UserRoleFilterBuilder } from '@/shared/filters/entity-filters/user-role-filter-builder/user-role-filter-builder';
import { useReadAllRolesQuery } from '@/shared/redux/api/ident/ident-api';

const GroupRolesListInner = () => {
  const { isReady } = useFilterScope();
  const { groupId } = useParams();
  const { transformRole } = useRolesTransformer();
  const { useTenantSettings, permissionActions: rbacPermissionActions } =
    useRbacConfig();
  const { ssoEnabled } = useTenantSettings();
  const {
    resourceLists,
    groupRoutePath,
    permissionActions: groupActions,
  } = useGroupsConfig();
  const listConfig = resourceLists?.roles;
  const { can } = usePermissionCheck();
  const { pagination, filter } = useGroupedResourceQueryParams(
    groupId,
    new UserRoleFilterBuilder(),
  );
  const { data, isLoading, isFetching } = useReadAllRolesQuery(
    { filter, pagination },
    { skip: !isReady },
  );

  const colDefs = useGroupRolesListDefinitions({
    ssoEnabled,
    actionsRenderer: listConfig?.rowActions,
  });
  const rowData = data?.readAllUserRoles.items.map(transformRole);
  const canManageGroup = can(groupActions.updateGroup, groupId);
  const canUpdateRole = (item: TTransformedRole) =>
    can(rbacPermissionActions.updateUserRole, item.userRoleId);

  const getItemId = (item: TTransformedRole) => item.userRoleId;
  const getItemLink = (item: TTransformedRole) =>
    listConfig?.getItemLink?.(groupId ?? '', item.userRoleId) ??
    `${groupRoutePath}/${groupId}/roles/${item.userRoleId}`;
  const disableItemCheckbox = (item: TTransformedRole) => !canUpdateRole(item);

  const getBatchActions = () => {
    const render = listConfig?.renderBatchActions;
    if (!canManageGroup || !render || !rowData?.some(canUpdateRole)) {
      return undefined;
    }

    return (
      selectedItems: TTransformedRole[],
      setSelectedItems: Dispatch<SetStateAction<TTransformedRole[]>>,
    ) => {
      if (
        !can(rbacPermissionActions.updateUserRole, selectedItems.map(getItemId))
      ) {
        return null;
      }

      return render(
        selectedItems.map(({ raw }) => raw),
        () => setSelectedItems([]),
      );
    };
  };

  const _data: TData<TTransformedRole> = {
    isFetching,
    isLoading,
    items: rowData || [],
    pages: data?.readAllUserRoles.pages ?? 0,
    itemCount: data?.readAllUserRoles.itemCount ?? 0,
  };

  return (
    <DataTableProvider<TTransformedRole>
      value={{
        data: _data,
        getItemId,
        getItemLink,
        disableItemCheckbox,
      }}
      colDefs={colDefs}
    >
      <InnerDataTable<TTransformedRole> batchActions={getBatchActions()} />
    </DataTableProvider>
  );
};

export const GroupRolesList = () => (
  <FilterScopeProvider scopeKey="group.roles.list">
    <GroupRolesListInner />
  </FilterScopeProvider>
);
